import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch, notFoundIdHandler } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { TipoCliente, TipoClientePagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchTiposCliente = (params?: GetTiposClienteParams) => {
  return useQuery({
    queryKey: ['tiposCliente', ...Object.values(params || {})],
    queryFn: async () => {
      return getTiposCliente(params);
    },
  });
};

export const useGetTipoCliente = (id: string) => {
  return useQuery({
    queryKey: ['tipoCliente', id],
    queryFn: async () => {
      return getTipoCliente(id);
    },
  });
};

export const useCreateTipoCliente = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposCliente'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateTipoCliente = (
  navigate: NavigateFunction,
  returnUrl: string,
  redirect = true,
  keyToInvalidate = 'tiposCliente'
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyToInvalidate] });
    },
    onSettled: () => {
      redirect && navigate(returnUrl);
    },
  });
};

///* axios
export type GetTiposClienteParams = {
  page?: number;
  page_size?: number;

  // search
  nombre?: string;
};

export type CreateTipoClienteParams = Omit<TipoCliente, 'id_tipo_cliente'>;
export type UpdateTipoClienteParams = {
  id: string;
  data: CreateTipoClienteParams;
};

export const getTiposCliente = async (params?: GetTiposClienteParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<TipoClientePagingRes>(
    `${urlBackend}api/v1/tipos-cliente/?${queryParams}`,
    true
  );
};

export const getTipoCliente = async (id: string) => {
  const res = await get<TipoCliente>(
    `${urlBackend}api/v1/tipos-cliente/${id}/`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createTipoCliente = async (data: CreateTipoClienteParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TipoCliente>(
    `${urlBackend}api/v1/tipos-cliente/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoCliente = async ({
  id,
  data,
}: UpdateTipoClienteParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TipoCliente>(
    `${urlBackend}api/v1/tipos-cliente/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
